/**
 * Sample building AI register — seed data for `/samples/building-ai-register`.
 *
 * Public marketing surface. Illustrates the Building lane story: internal
 * + shipped AI systems, per-system evidence trail, EU AI Act deployer-role
 * classification, questionnaire renderability.
 *
 * Categories span the full "internal AI" scope per positioning-v3 §Meta-frame:
 * custom-built, API-integrated, SaaS-embedded (deployer role), DIY.
 */

export type BuildingDisposition =
  | 'governed'
  | 'pending_review'
  | 'awaiting_classification'
  | 'outside_oversight_scope'
  | 'retired';

export type SystemCategory =
  | 'custom_built'
  | 'api_integrated'
  | 'saas_embedded'
  | 'diy_llm'
  | 'shipped_to_customers';

export type EuAiActTier = 'unacceptable' | 'high_risk' | 'limited_risk' | 'minimal_risk' | 'gpai';

export interface BuildingSystemRow {
  id: string;
  systemName: string;
  category: SystemCategory;
  scope: 'internal' | 'shipped';
  disposition: BuildingDisposition;
  owner: { displayName: string; role: string };
  lastEvaluated: string | null;
  nextEvaluationDue: string | null;
  frameworkTags: string[];
  euAiActTier: EuAiActTier;
  hasEvaluationRecord: boolean;
  hasDataLineage: boolean;
  attestedSigner: { displayName: string; role: string } | null;
  questionnaireReady: boolean;
  incidentsOpen: number;
  narrative: string;
}

const T = (offsetDays: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString();
};

export const BUILDING_ROWS: BuildingSystemRow[] = [
  {
    id: 'sys-001',
    systemName: 'PatientTriage Assist (shipped)',
    category: 'custom_built',
    scope: 'shipped',
    disposition: 'governed',
    owner: { displayName: 'Anika Sharma', role: 'CISO' },
    lastEvaluated: T(-14),
    nextEvaluationDue: T(76),
    frameworkTags: ['NIST AI RMF', 'ISO 42001', 'HIPAA + AI', 'EU AI Act'],
    euAiActTier: 'high_risk',
    hasEvaluationRecord: true,
    hasDataLineage: true,
    attestedSigner: { displayName: 'Priya Ramanathan', role: 'VP Legal' },
    questionnaireReady: true,
    incidentsOpen: 0,
    narrative: "Our product's patient triage assistant. Ships to HealthTech customers. EU AI Act high-risk classification confirmed. HIPAA § 164.514 de-identification pipeline documented. Six named signers across product, legal, and clinical.",
  },
  {
    id: 'sys-002',
    systemName: 'RiskScore Copilot (internal)',
    category: 'api_integrated',
    scope: 'internal',
    disposition: 'governed',
    owner: { displayName: 'Marcus Chen', role: 'Head of Eng' },
    lastEvaluated: T(-30),
    nextEvaluationDue: T(60),
    frameworkTags: ['NIST AI RMF', 'SOC 2 + AI'],
    euAiActTier: 'limited_risk',
    hasEvaluationRecord: true,
    hasDataLineage: true,
    attestedSigner: { displayName: 'Marcus Chen', role: 'Head of Eng' },
    questionnaireReady: true,
    incidentsOpen: 0,
    narrative: 'Anthropic Claude via API for internal risk-scoring on new supplier onboarding. Data-boundary: PII redacted before API call. Weekly evaluation record.',
  },
  {
    id: 'sys-003',
    systemName: 'M365 Copilot (tenant deployment)',
    category: 'saas_embedded',
    scope: 'internal',
    disposition: 'governed',
    owner: { displayName: 'Anika Sharma', role: 'CISO' },
    lastEvaluated: T(-7),
    nextEvaluationDue: T(83),
    frameworkTags: ['NIST AI RMF', 'ISO 42001', 'EU AI Act'],
    euAiActTier: 'limited_risk',
    hasEvaluationRecord: true,
    hasDataLineage: true,
    attestedSigner: { displayName: 'Elena Vartan', role: 'General Counsel' },
    questionnaireReady: true,
    incidentsOpen: 0,
    narrative: 'Microsoft Copilot deployed across 2,400 seats. Deployer role: FIRST-PARTY per EU AI Act Art 3(4) — data is ours, deployment decision is ours. Sensitivity labels enforced; sharepoint content boundary documented.',
  },
  {
    id: 'sys-004',
    systemName: 'ShipmentETA ML (shipped)',
    category: 'custom_built',
    scope: 'shipped',
    disposition: 'governed',
    owner: { displayName: 'Marcus Chen', role: 'Head of Eng' },
    lastEvaluated: T(-45),
    nextEvaluationDue: T(45),
    frameworkTags: ['NIST AI RMF', 'SOC 2 + AI'],
    euAiActTier: 'minimal_risk',
    hasEvaluationRecord: true,
    hasDataLineage: true,
    attestedSigner: { displayName: 'Marcus Chen', role: 'Head of Eng' },
    questionnaireReady: true,
    incidentsOpen: 0,
    narrative: 'Delivery-window prediction model shipped in logistics product. Trained on aggregated route data, no PII. Q3 model card current.',
  },
  {
    id: 'sys-005',
    systemName: 'GitHub Copilot Business',
    category: 'saas_embedded',
    scope: 'internal',
    disposition: 'pending_review',
    owner: { displayName: 'Marcus Chen', role: 'Head of Eng' },
    lastEvaluated: T(-108),
    nextEvaluationDue: T(-18),
    frameworkTags: ['SOC 2 + AI'],
    euAiActTier: 'minimal_risk',
    hasEvaluationRecord: true,
    hasDataLineage: false,
    attestedSigner: { displayName: 'Marcus Chen', role: 'Head of Eng' },
    questionnaireReady: false,
    incidentsOpen: 1,
    narrative: 'Deployed for 180 engineering seats. Data-lineage documentation flagged incomplete — training-data provenance for suggestion model is missing. 18 days past next-review due date.',
  },
  {
    id: 'sys-006',
    systemName: 'Salesforce Einstein (Sales Cloud)',
    category: 'saas_embedded',
    scope: 'internal',
    disposition: 'awaiting_classification',
    owner: { displayName: 'Anika Sharma', role: 'CISO' },
    lastEvaluated: null,
    nextEvaluationDue: T(60),
    frameworkTags: ['NIST AI RMF'],
    euAiActTier: 'limited_risk',
    hasEvaluationRecord: false,
    hasDataLineage: false,
    attestedSigner: null,
    questionnaireReady: false,
    incidentsOpen: 0,
    narrative: 'Sales activated Einstein Copilot Q3 2026 under existing Salesforce contract. Deployer role classification in progress: our data + our deployment decision → first-party. Baseline 90-day classification period active.',
  },
  {
    id: 'sys-007',
    systemName: 'Support Ticket Summarization (internal DIY)',
    category: 'diy_llm',
    scope: 'internal',
    disposition: 'pending_review',
    owner: { displayName: 'Priya Ramanathan', role: 'VP Legal' },
    lastEvaluated: T(-92),
    nextEvaluationDue: T(-2),
    frameworkTags: ['NIST AI RMF'],
    euAiActTier: 'minimal_risk',
    hasEvaluationRecord: true,
    hasDataLineage: true,
    attestedSigner: null,
    questionnaireReady: false,
    incidentsOpen: 0,
    narrative: 'Support team built an OpenAI-API integration to summarize incoming tickets. No product surface. Evaluation refresh due — flagged for review.',
  },
  {
    id: 'sys-008',
    systemName: 'FraudNet (shipped, high-risk)',
    category: 'custom_built',
    scope: 'shipped',
    disposition: 'governed',
    owner: { displayName: 'Anika Sharma', role: 'CISO' },
    lastEvaluated: T(-4),
    nextEvaluationDue: T(86),
    frameworkTags: ['NIST AI RMF', 'ISO 42001', 'EU AI Act', 'SR 11-7'],
    euAiActTier: 'high_risk',
    hasEvaluationRecord: true,
    hasDataLineage: true,
    attestedSigner: { displayName: 'Elena Vartan', role: 'General Counsel' },
    questionnaireReady: true,
    incidentsOpen: 0,
    narrative: 'Real-time fraud detection shipped to FinTech customers. EU AI Act high-risk (credit-access decision assist). SR 11-7 model governance in place. Audit-committee attestation current.',
  },
];

export const BUILDING_SUMMARY = {
  totalSystems: BUILDING_ROWS.length,
  governed: BUILDING_ROWS.filter((r) => r.disposition === 'governed').length,
  pendingReview: BUILDING_ROWS.filter((r) => r.disposition === 'pending_review').length,
  awaitingClassification: BUILDING_ROWS.filter((r) => r.disposition === 'awaiting_classification').length,
  outsideOversightScope: BUILDING_ROWS.filter((r) => r.disposition === 'outside_oversight_scope').length,
  shippedToCustomers: BUILDING_ROWS.filter((r) => r.scope === 'shipped').length,
  internalOnly: BUILDING_ROWS.filter((r) => r.scope === 'internal').length,
  questionnaireReady: BUILDING_ROWS.filter((r) => r.questionnaireReady).length,
  highRiskEuAiAct: BUILDING_ROWS.filter((r) => r.euAiActTier === 'high_risk').length,
  frameworkCoverage: Math.round(
    (BUILDING_ROWS.filter((r) => r.hasEvaluationRecord && r.hasDataLineage).length /
      BUILDING_ROWS.length) *
      100,
  ),
};

export const BUILDING_DISPOSITION_LABELS: Record<BuildingDisposition, string> = {
  governed: 'Under systemic oversight',
  pending_review: 'Pending review',
  awaiting_classification: 'Awaiting classification',
  outside_oversight_scope: 'Outside oversight scope',
  retired: 'Retired',
};

export const CATEGORY_LABELS: Record<SystemCategory, string> = {
  custom_built: 'Custom-built',
  api_integrated: 'API-integrated',
  saas_embedded: 'SaaS-embedded (deployer role)',
  diy_llm: 'DIY LLM integration',
  shipped_to_customers: 'Shipped to customers',
};

export const EU_AI_ACT_LABELS: Record<EuAiActTier, string> = {
  unacceptable: 'Unacceptable risk',
  high_risk: 'High risk',
  limited_risk: 'Limited risk',
  minimal_risk: 'Minimal risk',
  gpai: 'GPAI',
};
